import { useState, useEffect, useRef } from "react";

const NAV_LINKS = ["Home", "About", "Services", "Industries", "Contact"];

const SERVICES = [
  { icon: "⬡", title: "Advanced WordPress", desc: "Custom themes, headless architecture, WooCommerce customization, and enterprise-level security hardening.", tags: ["Custom Themes", "Headless WP", "WooCommerce"], color: "#6EE7B7" },
  { icon: "◈", title: "Custom Web Development", desc: "Fully custom platforms built for performance — SaaS, portals, dashboards, CRM/ERP integrations.", tags: ["SaaS Platforms", "API Integrations", "Portals"], color: "#93C5FD" },
  { icon: "◎", title: "E-Commerce Solutions", desc: "Revenue-focused stores with conversion-optimized checkouts, subscriptions, and global payment gateways.", tags: ["WooCommerce", "Subscriptions", "Marketplace"], color: "#F9A8D4" },
  { icon: "◬", title: "UI/UX & Conversion Engineering", desc: "Design that converts — user journey mapping, wireframes, brand-aligned visual systems, mobile-first.", tags: ["UX Research", "Prototyping", "Mobile-first"], color: "#FCD34D" },
];

const INDUSTRIES = [
  { name: "Technology & SaaS", icon: "⚡" },
  { name: "E-Commerce & D2C", icon: "◈" },
  { name: "Education", icon: "◎" },
  { name: "Corporate Services", icon: "◬" },
];

const STATS = [
  { value: "150+", label: "Projects Delivered" },
  { value: "40+", label: "Global Clients" },
  { value: "98%", label: "Client Satisfaction" },
  { value: "5+", label: "Years Experience" },
];

const PROCESS = [
  { num: "01", title: "Business Discovery", desc: "We understand your goals, market positioning, and revenue strategy." },
  { num: "02", title: "Architecture & Strategy", desc: "We design a scalable technical and UX framework tailored to your needs." },
  { num: "03", title: "Premium UI Design", desc: "Clean, modern, high-conversion interface design aligned with your brand." },
  { num: "04", title: "Precision Development", desc: "Secure, optimized, and scalable coding to industry-leading standards." },
  { num: "05", title: "Testing & Optimization", desc: "Performance testing, security validation, and multi-device QA." },
  { num: "06", title: "Launch & Growth Support", desc: "Post-launch optimization, analytics monitoring, and continuous improvements." },
];

const TESTIMONIALS = [
  { quote: "Sparkmind transformed our online presence. Our conversion rate jumped 40% within two months of launch.", name: "Arjun Mehta", role: "Founder, TechScale India", avatar: "AM" },
  { quote: "The team's strategic thinking and execution quality is exceptional. They feel like a true technology partner.", name: "Sarah Collins", role: "Head of Digital, Veridian UK", avatar: "SC" },
  { quote: "From discovery to deployment, every phase was transparent and on-schedule. Exceptional professionals.", name: "Rohan Desai", role: "CEO, NovaMart", avatar: "RD" },
];

const PRICING = [
  { tier: "Business Website", price: "₹30,000", desc: "Professional web presence for growing businesses", features: ["Custom Design", "Mobile Responsive", "SEO Ready", "3 Months Support"] },
  { tier: "E-Commerce / WordPress", price: "₹50,000", desc: "Advanced solutions for serious online businesses", features: ["Everything in Business", "WooCommerce Setup", "Payment Integration", "Performance Optimization"], highlight: true },
  { tier: "Custom Application", price: "Custom", desc: "Enterprise SaaS and bespoke web applications", features: ["Full Discovery Phase", "Custom Architecture", "API & CRM Integration", "Dedicated Partnership"] },
];

export default function SparkMind() {
  const [activeNav, setActiveNav] = useState("Home");
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [contactForm, setContactForm] = useState({ name: "", email: "", project: "", message: "" });
  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);

  useEffect(() => {
    const check = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setActiveTestimonial(t => (t + 1) % TESTIMONIALS.length), 4000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setActiveNav(id.charAt(0).toUpperCase() + id.slice(1));
    setMenuOpen(false);
  };

  const sm = windowWidth < 640;
  const md = windowWidth >= 640 && windowWidth < 1024;

  const px = sm ? "20px" : "32px";
  const sectionPad = sm ? "72px" : "120px";

  return (
    <div style={{ fontFamily: "'Sora', sans-serif", background: "#06070A", color: "#E8EAF0", minHeight: "100vh", overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #0d0e14; }
        ::-webkit-scrollbar-thumb { background: #2a6fff; border-radius: 4px; }
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-18px)} }
        @keyframes slide-up { from{opacity:0;transform:translateY(32px)} to{opacity:1;transform:translateY(0)} }
        @keyframes shimmer { 0%{background-position:200% center} 100%{background-position:-200% center} }
        @keyframes spin-slow { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes slideDown { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:translateY(0)} }
        .hero-anim { animation: slide-up .9s cubic-bezier(.22,1,.36,1) both; }
        .hero-anim-2 { animation: slide-up .9s .18s cubic-bezier(.22,1,.36,1) both; }
        .hero-anim-3 { animation: slide-up .9s .34s cubic-bezier(.22,1,.36,1) both; }
        .float-el { animation: float 5s ease-in-out infinite; }
        .spin-el { animation: spin-slow 30s linear infinite; }
        .spin-el-r { animation: spin-slow 20s linear infinite reverse; }
        .shimmer-text { background: linear-gradient(90deg, #6EE7B7 0%, #93C5FD 30%, #F9A8D4 60%, #6EE7B7 100%); background-size: 200% auto; -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; animation: shimmer 4s linear infinite; }
        .card-hover { transition: transform .3s ease, box-shadow .3s ease; }
        .card-hover:hover { transform: translateY(-5px); box-shadow: 0 20px 50px rgba(42,111,255,.15); }
        .glass { background: rgba(255,255,255,.04); backdrop-filter: blur(20px); border: 1px solid rgba(255,255,255,.08); }
        .section-label { font-size: 12px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; color: #6EE7B7; }
        .btn-primary { background: linear-gradient(135deg, #2a6fff 0%, #1a4fd6 100%); border: none; color: #fff; border-radius: 10px; font-weight: 600; cursor: pointer; transition: transform .2s, box-shadow .2s; font-family: inherit; letter-spacing: .3px; }
        .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(42,111,255,.4); }
        .btn-outline { background: transparent; border: 1.5px solid rgba(232,234,240,.2); color: #E8EAF0; border-radius: 10px; font-weight: 500; cursor: pointer; transition: border-color .2s, background .2s; font-family: inherit; }
        .btn-outline:hover { border-color: #2a6fff; background: rgba(42,111,255,.08); }
        .tag { background: rgba(255,255,255,.06); border: 1px solid rgba(255,255,255,.1); padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 500; color: rgba(232,234,240,.65); }
        .mobile-menu-enter { animation: slideDown .25s ease both; }
        input, textarea, select { background: rgba(255,255,255,.04); border: 1px solid rgba(255,255,255,.1); color: #E8EAF0; padding: 14px 16px; border-radius: 10px; font-family: inherit; font-size: 15px; width: 100%; outline: none; transition: border-color .2s; resize: vertical; -webkit-appearance: none; appearance: none; }
        input:focus, textarea:focus, select:focus { border-color: #2a6fff; background: rgba(42,111,255,.04); }
        input::placeholder, textarea::placeholder { color: rgba(232,234,240,.3); }
        select option { background: #1a1c24; color: #E8EAF0; }
        .nav-dot { width: 6px; height: 6px; border-radius: 50%; border: none; cursor: pointer; transition: all .3s; }
      `}</style>

      {/* ═══ NAV ═══ */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 999,
        transition: "all .3s",
        background: scrolled || menuOpen ? "rgba(6,7,10,.95)" : "transparent",
        backdropFilter: scrolled || menuOpen ? "blur(24px)" : "none",
        borderBottom: scrolled && !menuOpen ? "1px solid rgba(255,255,255,.07)" : "none",
      }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: `${scrolled ? "12px" : "18px"} ${px}`, display: "flex", alignItems: "center", justifyContent: "space-between", transition: "padding .3s" }}>
          {/* Logo */}
          <div style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer" }} onClick={() => scrollTo("home")}>
            <div style={{ width: 32, height: 32, background: "linear-gradient(135deg, #2a6fff, #6EE7B7)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, flexShrink: 0 }}>S</div>
            <span style={{ fontSize: 17, fontWeight: 700, letterSpacing: "-.2px" }}>Sparkmind<span style={{ color: "#2a6fff" }}>.</span></span>
          </div>

          {/* Desktop links */}
          {!sm && (
            <div style={{ display: "flex", gap: md ? 20 : 28, alignItems: "center" }}>
              {NAV_LINKS.map(l => (
                <span key={l} onClick={() => scrollTo(l.toLowerCase())} style={{ fontSize: 14, fontWeight: 500, color: activeNav === l ? "#fff" : "rgba(232,234,240,.6)", cursor: "pointer", transition: "color .2s", position: "relative", paddingBottom: 2 }}>{l}</span>
              ))}
              <button className="btn-primary" style={{ padding: "10px 20px", fontSize: 14 }} onClick={() => scrollTo("contact")}>Get Started</button>
            </div>
          )}

          {/* Hamburger */}
          {sm && (
            <button onClick={() => setMenuOpen(o => !o)} style={{ background: "none", border: "none", cursor: "pointer", padding: "6px", display: "flex", flexDirection: "column", gap: 5 }} aria-label="Menu">
              <span style={{ display: "block", width: 22, height: 2, background: "#E8EAF0", borderRadius: 2, transition: "all .3s", transform: menuOpen ? "rotate(45deg) translate(5px, 5px)" : "none" }} />
              <span style={{ display: "block", width: 22, height: 2, background: "#E8EAF0", borderRadius: 2, transition: "all .3s", opacity: menuOpen ? 0 : 1 }} />
              <span style={{ display: "block", width: 22, height: 2, background: "#E8EAF0", borderRadius: 2, transition: "all .3s", transform: menuOpen ? "rotate(-45deg) translate(5px, -5px)" : "none" }} />
            </button>
          )}
        </div>

        {/* Mobile menu */}
        {sm && menuOpen && (
          <div className="mobile-menu-enter" style={{ borderTop: "1px solid rgba(255,255,255,.07)", padding: "16px 20px 24px" }}>
            {NAV_LINKS.map(l => (
              <div key={l} onClick={() => scrollTo(l.toLowerCase())} style={{ padding: "14px 16px", fontSize: 16, fontWeight: 500, color: activeNav === l ? "#fff" : "rgba(232,234,240,.7)", borderRadius: 10, cursor: "pointer", background: activeNav === l ? "rgba(42,111,255,.1)" : "transparent", marginBottom: 4, transition: "background .2s" }}>{l}</div>
            ))}
            <button className="btn-primary" style={{ width: "100%", padding: "16px", fontSize: 15, marginTop: 12 }} onClick={() => scrollTo("contact")}>Get Started →</button>
          </div>
        )}
      </nav>

      {/* ═══ HERO ═══ */}
      <section id="home" style={{ minHeight: "100vh", display: "flex", alignItems: "center", position: "relative", overflow: "hidden", padding: `0 ${px}` }}>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 80% 60% at 60% 40%, rgba(42,111,255,.12) 0%, transparent 70%)" }} />
        <div style={{ position: "absolute", top: "12%", right: "4%", width: sm ? 180 : 420, height: sm ? 180 : 420, borderRadius: "50%", background: "radial-gradient(circle, rgba(110,231,183,.07) 0%, transparent 70%)" }} className="float-el" />
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.02) 1px, transparent 1px)", backgroundSize: "60px 60px", maskImage: "radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)" }} />

        <div style={{ maxWidth: 1200, margin: "0 auto", width: "100%", paddingTop: sm ? 110 : 130, paddingBottom: sm ? 72 : 90, display: "grid", gridTemplateColumns: sm || md ? "1fr" : "1fr 1fr", gap: 60, alignItems: "center" }}>
          <div>
            <div className="hero-anim" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(42,111,255,.12)", border: "1px solid rgba(42,111,255,.25)", padding: "7px 14px", borderRadius: 20, marginBottom: 24 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#6EE7B7", animation: "blink 1.5s ease infinite", flexShrink: 0 }} />
              <span style={{ fontSize: sm ? 11 : 13, color: "#93C5FD", fontWeight: 500 }}>Based in Bangalore · Delivering Globally</span>
            </div>
            <h1 className="hero-anim-2" style={{ fontSize: sm ? "38px" : md ? "50px" : "clamp(46px, 5vw, 68px)", fontWeight: 800, lineHeight: 1.1, letterSpacing: "-2px", marginBottom: 20 }}>
              Engineering<br /><span className="shimmer-text">Digital Growth</span><br />for Ambitious Brands
            </h1>
            <p className="hero-anim-3" style={{ fontSize: sm ? 15 : 17, color: "rgba(232,234,240,.6)", lineHeight: 1.75, maxWidth: 500, marginBottom: 36, fontWeight: 300 }}>
              We build powerful, scalable, revenue-driven digital platforms — designed for performance, authority, and measurable business growth.
            </p>
            <div className="hero-anim-3" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <button className="btn-primary" style={{ padding: sm ? "13px 24px" : "14px 32px", fontSize: sm ? 14 : 15 }} onClick={() => scrollTo("services")}>Explore Solutions</button>
              <button className="btn-outline" style={{ padding: sm ? "13px 24px" : "14px 32px", fontSize: sm ? 14 : 15 }} onClick={() => scrollTo("contact")}>Let's Talk →</button>
            </div>
            <div className="hero-anim-3" style={{ display: "flex", gap: sm ? 24 : 36, marginTop: 44, flexWrap: "wrap" }}>
              {STATS.slice(0, 3).map(s => (
                <div key={s.label}>
                  <div style={{ fontSize: sm ? 26 : 30, fontWeight: 800, color: "#fff", letterSpacing: "-1px" }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: "rgba(232,234,240,.4)" }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Orbital visual — desktop only */}
          {!sm && !md && (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div style={{ width: 440, height: 440, position: "relative" }}>
                <div className="spin-el" style={{ position: "absolute", inset: 0, borderRadius: "50%", border: "1px dashed rgba(42,111,255,.2)" }} />
                <div className="spin-el-r" style={{ position: "absolute", inset: 20, borderRadius: "50%", border: "1px dashed rgba(110,231,183,.15)" }} />
                <div className="glass" style={{ position: "absolute", inset: 60, borderRadius: 28, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10 }}>
                  <div style={{ fontSize: 52, fontWeight: 900, background: "linear-gradient(135deg, #2a6fff, #6EE7B7)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>S</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "rgba(232,234,240,.8)" }}>Sparkmind</div>
                  <div style={{ width: 36, height: 2, background: "linear-gradient(90deg, #2a6fff, #6EE7B7)", borderRadius: 2 }} />
                  <div style={{ fontSize: 11, color: "rgba(232,234,240,.4)" }}>Digital Excellence</div>
                </div>
                {[{ label: "WordPress", color: "#6EE7B7", angle: 0 }, { label: "React", color: "#93C5FD", angle: 90 }, { label: "Laravel", color: "#F9A8D4", angle: 180 }, { label: "SaaS", color: "#FCD34D", angle: 270 }].map(({ label, color, angle }) => {
                  const rad = (angle * Math.PI) / 180;
                  return (
                    <div key={label} className="glass" style={{ position: "absolute", left: 220 + 185 * Math.cos(rad) - 38, top: 220 + 185 * Math.sin(rad) - 14, padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, color, whiteSpace: "nowrap" }}>{label}</div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ═══ ABOUT ═══ */}
      <section id="about" style={{ padding: `${sectionPad} ${px}` }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: sm || md ? "1fr" : "1fr 1fr", gap: sm ? 48 : 80, alignItems: "center" }}>
            <div>
              <div className="section-label" style={{ marginBottom: 14 }}>About Sparkmind</div>
              <h2 style={{ fontSize: sm ? "30px" : "clamp(30px, 4vw, 50px)", fontWeight: 800, letterSpacing: "-1.5px", lineHeight: 1.15, marginBottom: 18 }}>
                A Premium Technology Partner Built for <span style={{ color: "#2a6fff" }}>Real Growth</span>
              </h2>
              <p style={{ color: "rgba(232,234,240,.6)", lineHeight: 1.8, fontSize: sm ? 15 : 16, marginBottom: 16, fontWeight: 300 }}>
                Headquartered in Bangalore — one of the world's fastest-growing technology hubs — Sparkmind Solutions partners with growth-focused businesses across India, the US, the UK, and global markets.
              </p>
              <p style={{ color: "rgba(232,234,240,.6)", lineHeight: 1.8, fontSize: sm ? 15 : 16, marginBottom: 32, fontWeight: 300 }}>
                We combine strategic thinking, modern UI/UX, and clean secure development to create digital assets that generate real, measurable ROI — not just websites, but complete digital systems.
              </p>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {["Strategic", "Scalable", "Results-Driven"].map(t => <span key={t} className="tag">{t}</span>)}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              {STATS.map((s, i) => (
                <div key={s.label} className="glass card-hover" style={{ padding: sm ? 22 : 30, borderRadius: 20, textAlign: i % 2 === 1 ? "right" : "left" }}>
                  <div style={{ fontSize: sm ? 30 : 38, fontWeight: 800, letterSpacing: "-2px", background: "linear-gradient(135deg, #fff, rgba(232,234,240,.5))", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{s.value}</div>
                  <div style={{ fontSize: 13, color: "rgba(232,234,240,.5)", marginTop: 4 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SERVICES ═══ */}
      <section id="services" style={{ padding: `${sectionPad} ${px}`, background: "rgba(255,255,255,.015)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: sm ? 44 : 68 }}>
            <div className="section-label" style={{ marginBottom: 14 }}>What We Build</div>
            <h2 style={{ fontSize: sm ? "30px" : "clamp(30px, 4vw, 50px)", fontWeight: 800, letterSpacing: "-1.5px", lineHeight: 1.15 }}>
              Solutions Engineered for <span style={{ color: "#2a6fff" }}>Performance</span>
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: sm ? "1fr" : "1fr 1fr", gap: 16 }}>
            {SERVICES.map((s, i) => (
              <div key={s.title} className="glass card-hover" style={{ padding: sm ? "26px 22px" : "38px", borderRadius: 22 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
                  <span style={{ fontSize: 30, color: s.color }}>{s.icon}</span>
                  <span style={{ fontSize: 12, color: "rgba(232,234,240,.25)", fontWeight: 600 }}>0{i + 1}</span>
                </div>
                <h3 style={{ fontSize: sm ? 18 : 20, fontWeight: 700, marginBottom: 10, letterSpacing: "-.3px" }}>{s.title}</h3>
                <p style={{ color: "rgba(232,234,240,.55)", lineHeight: 1.7, fontSize: 14, marginBottom: 20, fontWeight: 300 }}>{s.desc}</p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {s.tags.map(t => <span key={t} className="tag" style={{ borderColor: `${s.color}30`, color: s.color }}>{t}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PROCESS ═══ */}
      <section style={{ padding: `${sectionPad} ${px}` }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: sm ? 44 : 68 }}>
            <div className="section-label" style={{ marginBottom: 14 }}>How We Work</div>
            <h2 style={{ fontSize: sm ? "30px" : "clamp(30px, 4vw, 50px)", fontWeight: 800, letterSpacing: "-1.5px" }}>
              Structured. Transparent. <span style={{ color: "#2a6fff" }}>Results-Oriented</span>
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: sm ? "1fr" : md ? "1fr 1fr" : "repeat(3, 1fr)", gap: 14 }}>
            {PROCESS.map(p => (
              <div key={p.num} className="glass card-hover" style={{ padding: sm ? "22px 20px" : "30px", borderRadius: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#2a6fff", letterSpacing: 2, marginBottom: 10 }}>{p.num}</div>
                <h4 style={{ fontSize: sm ? 16 : 17, fontWeight: 700, marginBottom: 8, letterSpacing: "-.3px" }}>{p.title}</h4>
                <p style={{ color: "rgba(232,234,240,.5)", fontSize: 13, lineHeight: 1.7, fontWeight: 300 }}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ INDUSTRIES ═══ */}
      <section id="industries" style={{ padding: `${sectionPad} ${px}`, background: "rgba(255,255,255,.015)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: sm || md ? "1fr" : "1fr 1fr", gap: sm ? 44 : 80, alignItems: "center" }}>
            <div>
              <div className="section-label" style={{ marginBottom: 14 }}>Industries</div>
              <h2 style={{ fontSize: sm ? "30px" : "clamp(30px, 4vw, 50px)", fontWeight: 800, letterSpacing: "-1.5px", lineHeight: 1.15, marginBottom: 18 }}>
                Serving <span style={{ color: "#2a6fff" }}>Diverse Industries</span> at Scale
              </h2>
              <p style={{ color: "rgba(232,234,240,.55)", lineHeight: 1.8, fontSize: sm ? 15 : 16, fontWeight: 300 }}>
                Whether you're a local Indian business or an international brand targeting US or UK markets, we build solutions that compete on a global stage.
              </p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {INDUSTRIES.map(ind => (
                <div key={ind.name} className="glass card-hover" style={{ padding: sm ? "14px 16px" : "20px 22px", borderRadius: 16, display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 17, color: "#6EE7B7", flexShrink: 0 }}>{ind.icon}</span>
                  <span style={{ fontSize: sm ? 12 : 14, fontWeight: 500, color: "rgba(232,234,240,.8)" }}>{ind.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ TESTIMONIALS ═══ */}
      <section style={{ padding: `${sectionPad} ${px}` }}>
        <div style={{ maxWidth: 840, margin: "0 auto", textAlign: "center" }}>
          <div className="section-label" style={{ marginBottom: 14 }}>Client Feedback</div>
          <h2 style={{ fontSize: sm ? "30px" : "clamp(30px, 4vw, 46px)", fontWeight: 800, letterSpacing: "-1.5px", marginBottom: sm ? 36 : 56 }}>
            Trusted by Builders & <span style={{ color: "#2a6fff" }}>Growth Leaders</span>
          </h2>
          <div style={{ position: "relative", minHeight: sm ? 300 : 260 }}>
            {TESTIMONIALS.map((t, i) => (
              <div key={t.name} style={{ transition: "opacity .5s ease", opacity: i === activeTestimonial ? 1 : 0, position: i === activeTestimonial ? "relative" : "absolute", top: 0, left: 0, right: 0 }}>
                <div className="glass" style={{ padding: sm ? "28px 20px" : "44px 52px", borderRadius: 24, marginBottom: 24 }}>
                  <div style={{ fontSize: 44, color: "#2a6fff", lineHeight: .5, marginBottom: 18, fontFamily: "Georgia, serif" }}>"</div>
                  <p style={{ fontSize: sm ? 14 : 17, color: "rgba(232,234,240,.85)", lineHeight: 1.75, marginBottom: 24, fontWeight: 300, fontStyle: "italic" }}>{t.quote}</p>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "center" }}>
                    <div style={{ width: 40, height: 40, borderRadius: "50%", background: "linear-gradient(135deg, #2a6fff, #6EE7B7)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{t.avatar}</div>
                    <div style={{ textAlign: "left" }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{t.name}</div>
                      <div style={{ fontSize: 12, color: "rgba(232,234,240,.4)" }}>{t.role}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 8 }}>
            {TESTIMONIALS.map((_, i) => (
              <button key={i} className="nav-dot" onClick={() => setActiveTestimonial(i)} style={{ width: i === activeTestimonial ? 26 : 8, background: i === activeTestimonial ? "#2a6fff" : "rgba(255,255,255,.2)" }} />
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CONTACT ═══ */}
      <section id="contact" style={{ padding: `${sectionPad} ${px}` }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: sm || md ? "1fr" : "1fr 1.2fr", gap: sm ? 48 : 80 }}>
            <div>
              <div className="section-label" style={{ marginBottom: 14 }}>Get In Touch</div>
              <h2 style={{ fontSize: sm ? "30px" : "clamp(30px, 4vw, 50px)", fontWeight: 800, letterSpacing: "-1.5px", lineHeight: 1.15, marginBottom: 18 }}>
                Ready to Build Something <span style={{ color: "#2a6fff" }}>Remarkable?</span>
              </h2>
              <p style={{ color: "rgba(232,234,240,.55)", lineHeight: 1.8, fontSize: sm ? 15 : 16, marginBottom: sm ? 32 : 40, fontWeight: 300 }}>
                Let's talk about your project. We'll understand your goals and create a clear roadmap to digital growth.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                {[
                  { icon: "◈", label: "Email", val: "info@sparkmindsolutions.com" },
                  { icon: "☎", label: "Phone", val: "+91 63629 22731  ·  +91 98868 12930" },
                  { icon: "◎", label: "Location", val: "Bangalore, Karnataka, India" },
                  { icon: "⬡", label: "Markets", val: "India · US · UK · Global" },
                ].map(item => (
                  <div key={item.label} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                    <div style={{ width: 40, height: 40, background: "rgba(42,111,255,.12)", border: "1px solid rgba(42,111,255,.2)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, color: "#2a6fff", flexShrink: 0 }}>{item.icon}</div>
                    <div>
                      <div style={{ fontSize: 11, color: "rgba(232,234,240,.4)", fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", marginBottom: 2 }}>{item.label}</div>
                      <div style={{ fontSize: 14, color: "rgba(232,234,240,.8)" }}>{item.val}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="glass" style={{ padding: sm ? "24px 18px" : 44, borderRadius: 24 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ display: "grid", gridTemplateColumns: sm ? "1fr" : "1fr 1fr", gap: 14 }}>
                  <div>
                    <label style={{ fontSize: 12, color: "rgba(232,234,240,.5)", display: "block", marginBottom: 7, fontWeight: 500 }}>Your Name</label>
                    <input placeholder="Rahul Kumar" value={contactForm.name} onChange={e => setContactForm(f => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: "rgba(232,234,240,.5)", display: "block", marginBottom: 7, fontWeight: 500 }}>Email Address</label>
                    <input placeholder="rahul@company.com" type="email" value={contactForm.email} onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))} />
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: "rgba(232,234,240,.5)", display: "block", marginBottom: 7, fontWeight: 500 }}>Project Type</label>
                  <select value={contactForm.project} onChange={e => setContactForm(f => ({ ...f, project: e.target.value }))}>
                    <option value="">Select a service...</option>
                    <option>WordPress Development</option>
                    <option>E-Commerce Solution</option>
                    <option>Custom Web Application</option>
                    <option>SaaS Platform</option>
                    <option>UI/UX Design</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: "rgba(232,234,240,.5)", display: "block", marginBottom: 7, fontWeight: 500 }}>Tell Us About Your Project</label>
                  <textarea rows={sm ? 4 : 5} placeholder="Describe your goals, timeline, and requirements..." value={contactForm.message} onChange={e => setContactForm(f => ({ ...f, message: e.target.value }))} />
                </div>
                <button className="btn-primary" style={{ width: "100%", padding: "15px", fontSize: 15 }}>Send Message →</button>
                <p style={{ fontSize: 12, color: "rgba(232,234,240,.3)", textAlign: "center" }}>We typically respond within 24 hours</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer style={{ borderTop: "1px solid rgba(255,255,255,.07)", padding: sm ? "44px 20px 28px" : "56px 32px 36px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: sm ? "1fr 1fr" : md ? "1fr 1fr 1fr" : "2fr 1fr 1fr 1fr", gap: sm ? 28 : 44, marginBottom: 44 }}>
            <div style={{ gridColumn: sm ? "1 / -1" : "auto" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 12 }}>
                <div style={{ width: 30, height: 30, background: "linear-gradient(135deg, #2a6fff, #6EE7B7)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800 }}>S</div>
                <span style={{ fontSize: 16, fontWeight: 700 }}>Sparkmind<span style={{ color: "#2a6fff" }}>.</span></span>
              </div>
              <p style={{ fontSize: 13, color: "rgba(232,234,240,.4)", lineHeight: 1.7, maxWidth: 260, fontWeight: 300 }}>Premium website development and digital engineering. Building digital systems for ambitious brands worldwide.</p>
            </div>
            {[
              { title: "Services", links: ["WordPress Dev", "Web Applications", "E-Commerce", "UI/UX Design"] },
              { title: "Industries", links: ["SaaS & Tech", "E-Commerce", "Healthcare", "Education"] },
              { title: "Company", links: ["About", "Process", "Industries", "Contact"] },
            ].map(col => (
              <div key={col.title}>
                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 2, textTransform: "uppercase", color: "rgba(232,234,240,.35)", marginBottom: 14 }}>{col.title}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {col.links.map(l => <span key={l} style={{ fontSize: 13, color: "rgba(232,234,240,.5)", cursor: "pointer", transition: "color .2s" }} onMouseOver={e => e.target.style.color = "#fff"} onMouseOut={e => e.target.style.color = "rgba(232,234,240,.5)"}>{l}</span>)}
                </div>
              </div>
            ))}
          </div>
          <div style={{ borderTop: "1px solid rgba(255,255,255,.06)", paddingTop: 24, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <span style={{ fontSize: 12, color: "rgba(232,234,240,.3)" }}>© 2026 Sparkmind Solutions. All rights reserved.</span>
            <span style={{ fontSize: 12, color: "rgba(232,234,240,.25)" }}>Strategic · Scalable · Results-Driven</span>
          </div>
        </div>
      </footer>
    </div>
  );
}